import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../components/ui/button';
import { 
  HelpCircle, 
  X, 
  ChevronRight, 
  ChevronDown,
  LayoutDashboard,
  TrendingUp,
  Calculator,
  ClipboardCheck,
  FileText,
  ArrowUpDown,
  Sparkles,
  Settings,
  CheckSquare,
  Target,
  LayoutGrid
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const HelpGuide = () => {
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  // Help sections in ALPHABETICAL ORDER
  const helpSections = [
    {
      id: 'actuals-import',
      title: 'Actuals Import',
      icon: ArrowUpDown,
      content: [
        {
          subtitle: 'Import Actual Data',
          text: 'Import your actual financial data from Excel or CSV files. The system supports incremental imports so existing data is preserved.'
        },
        {
          subtitle: 'How to Import',
          text: '1. Go to Budgets page\n2. Find an Approved budget\n3. Click "Import Actuals" button\n4. Upload your Excel/CSV file\n5. Select import mode and months'
        },
        {
          subtitle: 'Import Modes',
          text: '- Incremental: Updates only the months you select, preserves existing data for other months\n- Overwrite: Replaces all actuals data for selected months'
        },
        {
          subtitle: 'File Format',
          text: 'Your file must include Account Code column and monthly columns (Jul through Jun). Download the template for the correct format.'
        },
        {
          subtitle: 'Import History',
          text: 'View the history of all actuals imports including who imported, when, which months were updated, and how many items changed.'
        }
      ]
    },
    {
      id: 'approvals',
      title: 'Approvals',
      icon: CheckSquare,
      content: [
        {
          subtitle: 'CFO Approval Queue',
          text: 'The Approvals page shows all review requests submitted by BU managers when they disagree with financial variances. This is visible to CFOs and Admins only.'
        },
        {
          subtitle: 'Review Requests',
          text: 'Each request shows the account, month, variance details, reason for disagreement, and suggested action. Click on a request to expand and see full details.'
        },
        {
          subtitle: 'Accept/Reject',
          text: 'CFO can Accept (acknowledge the disagreement) or Reject (send back to submitter) each request. Add a comment to explain your decision.'
        },
        {
          subtitle: 'Notifications',
          text: 'You will receive in-app notifications when new requests are submitted and when your requests are reviewed.'
        }
      ]
    },
    {
      id: 'budget-vs-actuals',
      title: 'Budget vs Actuals',
      icon: TrendingUp,
      content: [
        {
          subtitle: 'Variance Analysis',
          text: 'Compare budgeted amounts against actual performance. Green indicates favorable variances, red indicates unfavorable.'
        },
        {
          subtitle: 'Filtering',
          text: 'Filter by Fiscal Year, Business Unit, and Status. Users with BU restrictions will only see their assigned BU data.'
        },
        {
          subtitle: 'List vs Tile View',
          text: 'Toggle between List View (default, compact table) and Tile View (visual cards). Use the toggle in the top-right of the page.'
        },
        {
          subtitle: 'Consolidated View',
          text: 'Admins and CFOs can click "Consolidated BvA" to see aggregated Budget vs Actual data across ALL Business Units for the selected fiscal year.'
        },
        {
          subtitle: 'Financial Summary',
          text: 'When viewing a single budget or consolidated view, scroll down to see the Financial Summary showing Sales, Gross Profit, Opex, and Net Profit with monthly breakdowns.'
        },
        {
          subtitle: 'Hide Zeros',
          text: 'Toggle "Hide Zeros" to filter out line items with no budget or actual values for cleaner analysis.'
        },
        {
          subtitle: 'Export',
          text: 'Export your analysis to Excel or PDF. Choose between Budget Only, Budget vs Actual, or Full Report formats.'
        }
      ]
    },
    {
      id: 'budgets',
      title: 'Budgets',
      icon: Calculator,
      content: [
        {
          subtitle: 'Interactive Dashboard',
          text: 'The top cards show budget counts by status. Click any card to filter: Total, Approved, Pending Approval, Drafts, or Rejected.'
        },
        {
          subtitle: 'List vs Tile View',
          text: 'Toggle between List View (default, compact table) and Tile View (visual cards). Use the toggle in the top-right of the page.'
        },
        {
          subtitle: 'Creating Budgets',
          text: 'Click "Create Budget" to start a new budget. The name is auto-generated using the format: BUCode_FiscalYear_Budget. New budgets start in Draft status with all Chart of Accounts line items.'
        },
        {
          subtitle: 'Budget Workflow',
          text: '1. Draft: Initial state, can be edited\n2. Submit for Approval: Send to approvers\n3. Pending Approval: Awaiting review\n4. Approved: Budget is finalized\n5. Rejected: Needs revision (will show reason)'
        },
        {
          subtitle: 'Import Actuals',
          text: 'For approved budgets, click "Import Actuals" to upload actual financial data from Excel/CSV. Supports incremental imports without losing existing data.'
        },
        {
          subtitle: 'Consolidated View',
          text: 'Admins and CFOs can toggle "Consolidated View" to see aggregated budget data across ALL Business Units for the selected fiscal year. Includes collapsible categories, financial summary, and hide zeros toggle.'
        },
        {
          subtitle: 'Financial Summary',
          text: 'When viewing a single budget or consolidated view, scroll down to see the Financial Summary showing Sales, Gross Profit, Opex, and Net Profit with monthly breakdowns.'
        },
        {
          subtitle: 'Budget Revisions',
          text: 'For approved budgets, you can create a revision which changes status back to Draft for editing while preserving original figures.'
        },
        {
          subtitle: 'Comments',
          text: 'Click the comment icon on any line item to add month-specific comments. Comments can be included in exports.'
        }
      ]
    },
    {
      id: 'cashflow',
      title: 'Cashflow Planning',
      icon: Calculator,
      content: [
        {
          subtitle: 'Overview',
          text: 'Plan and monitor cash position across business units. Create base, best case, and worst case scenarios.'
        },
        {
          subtitle: 'Creating Cashflow Plans',
          text: '1. Click "Create Cashflow Plan"\n2. Enter plan name and select Business Unit\n3. Choose Scenario Type (Base/Best/Worst Case)\n4. Optionally copy from an existing budget\n5. Set Opening Balance and Minimum Cash Threshold'
        },
        {
          subtitle: 'Cash Position Dashboard',
          text: 'View summary cards showing: Opening Balance, Total Inflows, Total Outflows, and Closing Balance with threshold alerts.'
        },
        {
          subtitle: 'Monthly vs Weekly View',
          text: 'Toggle between Monthly view (12 months) for long-term planning and Weekly view (8 weeks) for short-term cash management.'
        },
        {
          subtitle: 'Threshold Alerts',
          text: 'Set a minimum cash threshold. Months where closing balance falls below threshold are flagged with warning indicators.'
        },
        {
          subtitle: 'Scenario Planning',
          text: 'Create multiple scenarios (Base, Best Case, Worst Case) to compare different cashflow outcomes and plan accordingly.'
        }
      ]
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: LayoutDashboard,
      content: [
        {
          subtitle: 'Overview',
          text: 'The Dashboard provides a high-level view of your financial metrics including Total Revenue, Total Expenses, Net Income, and Budget Variance.'
        },
        {
          subtitle: 'Charts',
          text: 'View cashflow trends over time and identify top variances at a glance. Charts update based on your selected filters.'
        },
        {
          subtitle: 'Global Filters',
          text: 'Use the Business Unit and Date Period filters to focus on specific segments of your data. Non-admin users will only see their assigned BU.'
        }
      ]
    },
    {
      id: 'financial-review',
      title: 'Financial Review',
      icon: ClipboardCheck,
      content: [
        {
          subtitle: 'Starting a Review',
          text: 'Select a Budget and Review Month, then click "Start Review" to begin the monthly financial review process.'
        },
        {
          subtitle: 'Review Table',
          text: 'The table shows each account with Budget, Actual, and Variance columns for the selected month and YTD totals.'
        },
        {
          subtitle: 'Agree/Disagree',
          text: 'For each line item, click Agree to confirm or Disagree to raise a concern. Disagreements are sent to the CFO for review via the Approvals page.'
        },
        {
          subtitle: 'Disagree Workflow',
          text: 'When you disagree:\n1. Select a reason category\n2. Provide a detailed explanation\n3. Suggest an action (optional)\n4. Submit - creates a review request for CFO'
        },
        {
          subtitle: 'Lock Review',
          text: 'Once all items are reviewed, lock the review to finalize it. Locked reviews cannot be changed.'
        }
      ]
    },
    {
      id: 'forecasts',
      title: 'Forecasts',
      icon: Target,
      content: [
        {
          subtitle: 'Forecast Types',
          text: '- Base Case: Standard forecast\n- Best Case: Optimistic scenario\n- Worst Case: Conservative scenario\n- Rolling: Continuous 12-month forecast'
        },
        {
          subtitle: 'Creating Forecasts',
          text: 'Click "Create Forecast" to start. You can copy from an approved budget as a starting point, or start from scratch using the Chart of Accounts template.'
        },
        {
          subtitle: 'Naming Convention',
          text: 'Forecast names are auto-generated: BUCode_FiscalYear_ForecastType (e.g., ANLU_2025-2026_BaseCase)'
        },
        {
          subtitle: 'Forecast Workflow',
          text: '1. Draft: Initial state, can be edited\n2. Active: Forecast is in use\n3. Archived: Historical, no longer active'
        }
      ]
    },
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: HelpCircle,
      content: [
        {
          subtitle: 'Welcome to C-Ges FP&A',
          text: 'C-Ges FP&A is a comprehensive Financial Planning & Analysis platform designed to help you manage budgets, forecasts, track actuals, perform financial reviews, and generate insights.'
        },
        {
          subtitle: 'Navigation',
          text: 'Use the sidebar on the left to navigate between sections. The sidebar can be collapsed or pinned. Badge numbers indicate pending approvals.'
        },
        {
          subtitle: 'User Roles',
          text: '- Admin: Full access to all features and BUs\n- CFO: Approval authority and all BU access\n- BU Manager: Access to assigned BU only\n- User: Based on assigned permissions'
        },
        {
          subtitle: 'Theme',
          text: 'Toggle between light and dark mode using the sun/moon icon in the top right corner.'
        }
      ]
    },
    {
      id: 'import-export',
      title: 'Import & Export',
      icon: ArrowUpDown,
      content: [
        {
          subtitle: 'Import Types',
          text: 'Import Budgets, Actuals, or Cashflow data from Excel files. The system will validate the format before importing.'
        },
        {
          subtitle: 'Actuals Import',
          text: 'Import actual data into approved budgets:\n1. Click "Import Actuals" on any approved budget\n2. Choose Incremental or Overwrite mode\n3. Select which months to import\n4. Upload your Excel/CSV file'
        },
        {
          subtitle: 'Export Options',
          text: 'Export data with flexible filters:\n- Business Unit: Single or All\n- Fiscal Year: Select year\n- Data Type: Budget Only, Actuals Only, or Full Report'
        },
        {
          subtitle: 'Naming Convention',
          text: 'Exported files follow the naming convention: BUCode_FiscalYear_Type_Date (e.g., ANLU_2025-2026_Budget_2026-03-14.xlsx)'
        },
        {
          subtitle: 'Template Downloads',
          text: 'Download Excel templates that match the required format for each import type. Click "Download Template" before importing.'
        }
      ]
    },
    {
      id: 'insights',
      title: 'AI Insights',
      icon: Sparkles,
      content: [
        {
          subtitle: 'AI-Powered Analysis',
          text: 'Get intelligent insights about your financial data including trend analysis, anomaly detection, and recommendations.'
        },
        {
          subtitle: 'Generating Insights',
          text: 'Click "Generate Insights" to analyze your current data. Insights update based on your selected Business Unit and Date Period.'
        },
        {
          subtitle: 'Note',
          text: 'AI Insights require the Emergent LLM Key. If you run out of balance, go to Profile → Universal Key → Add Balance.'
        }
      ]
    },
    {
      id: 'reports',
      title: 'Reports',
      icon: FileText,
      content: [
        {
          subtitle: 'Report Types',
          text: 'Generate various financial reports including:\n- P&L Statement\n- Balance Sheet\n- Cash Flow Statement\n- Budget Summary'
        },
        {
          subtitle: 'Export Options',
          text: 'Export reports to Excel, PDF, or CSV format. Reports follow the standard naming convention.'
        }
      ]
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: Settings,
      content: [
        {
          subtitle: 'Global Settings',
          text: 'Configure timezone, currency, date format (DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD), and default fiscal year.'
        },
        {
          subtitle: 'User Management',
          text: 'Admins can create users, assign roles, set Job Titles (CEO, CFO, BU Manager, etc.), and grant CFO-level approval authority.'
        },
        {
          subtitle: 'Business Units',
          text: 'Manage Business Units including codes used in naming conventions. Each BU can have a fiscal year override.'
        },
        {
          subtitle: 'Chart of Accounts',
          text: 'View and manage the account structure used across all budgets and forecasts.'
        }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  if (!isOpen) {
    return (
      <Button 
        variant="ghost" 
        size="icon" 
        onClick={() => setIsOpen(true)}
        className={`rounded-sm ${isDark ? 'text-zinc-400 hover:text-white hover:bg-zinc-800' : ''}`}
        data-testid="help-guide-toggle"
      >
        <HelpCircle className="w-5 h-5" />
      </Button>
    );
  }

  const onClose = () => setIsOpen(false);

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div 
        className={`w-full max-w-2xl max-h-[80vh] rounded-sm overflow-hidden shadow-xl ${isDark ? 'bg-zinc-900' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-zinc-800' : 'border-slate-200'}`}>
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-emerald-500" />
            <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Help Guide</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-sm">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-64px)] p-4">
          <div className="space-y-2">
            {helpSections.map((section) => {
              const Icon = section.icon;
              const isExpanded = expandedSection === section.id;
              
              return (
                <div 
                  key={section.id}
                  className={`border rounded-sm overflow-hidden ${isDark ? 'border-zinc-800' : 'border-slate-200'}`}
                >
                  <button
                    onClick={() => toggleSection(section.id)}
                    className={`w-full flex items-center justify-between p-3 text-left transition-colors ${
                      isDark 
                        ? 'hover:bg-zinc-800' 
                        : 'hover:bg-slate-50'
                    } ${isExpanded ? (isDark ? 'bg-zinc-800' : 'bg-slate-50') : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {section.title}
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className={`w-5 h-5 ${isDark ? 'text-zinc-400' : 'text-slate-400'}`} />
                    ) : (
                      <ChevronRight className={`w-5 h-5 ${isDark ? 'text-zinc-400' : 'text-slate-400'}`} />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className={`p-4 border-t ${isDark ? 'border-zinc-800 bg-zinc-800/50' : 'border-slate-200 bg-slate-50'}`}>
                      <div className="space-y-4">
                        {section.content.map((item, idx) => (
                          <div key={idx}>
                            <h4 className={`font-medium text-sm mb-1 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                              {item.subtitle}
                            </h4>
                            <p className={`text-sm whitespace-pre-line ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                              {item.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Button 
        variant="ghost" 
        size="icon" 
        onClick={onClose}
        className={`rounded-sm ${isDark ? 'text-zinc-400 hover:text-white hover:bg-zinc-800' : ''}`}
        data-testid="help-guide-toggle"
      >
        <HelpCircle className="w-5 h-5" />
      </Button>
      {createPortal(modalContent, document.body)}
    </>
  );
};

export default HelpGuide;
